import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { LOGO_SRC } from "../components/RevoraCard";

const INPUT =
  "w-full border border-black/15 bg-white px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-volt focus:border-volt transition-colors duration-200";

export default function Portal() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const verifyToken = params.get("token");

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifyState, setVerifyState] = useState(verifyToken ? "pending" : null);

  useEffect(() => {
    if (localStorage.getItem("revora_portal")) navigate("/portal/dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!verifyToken) return;
    api.post("/portal/verify", { token: verifyToken })
      .then(() => setVerifyState("ok"))
      .catch((e) => { setVerifyState("fail"); setError(formatApiError(e)); });
  }, [verifyToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "signup") {
        await api.post("/portal/signup", { email, password, business_name: businessName });
        setMode("login");
        setNotice("Account created. Check your inbox for the verification link, then sign in.");
      } else {
        const { data } = await api.post("/portal/login", { email, password });
        localStorage.setItem("revora_portal", data.token);
        navigate("/portal/dashboard");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/portal/resend-verification", { email });
      setNotice("If that account exists and is unverified, a new link is on its way.");
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (verifyState) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6 noise" data-testid="portal-verify">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm bg-white rounded-sm p-8 text-center">
          <img src={LOGO_SRC} alt="Revora Reviews" className="h-9 w-auto mx-auto mb-8" />
          {verifyState === "pending" && <p className="text-black/60 text-sm">Verifying your email…</p>}
          {verifyState === "ok" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="font-display text-2xl font-black text-ink mb-2">Email verified</p>
              <p className="text-sm text-black/55 mb-6">Your Revora business portal is active. You can sign in now.</p>
              <Link to="/portal" data-testid="verify-login-link" className="inline-block bg-volt text-ink font-bold px-8 py-3.5 rounded-full hover:bg-voltdark hover:text-white transition-colors duration-300">
                Sign in
              </Link>
            </>
          )}
          {verifyState === "fail" && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="font-display text-2xl font-black text-ink mb-2">Link expired</p>
              <p className="text-sm text-black/55 mb-6">{error || "This verification link is invalid or has expired."}</p>
              <Link to="/portal" className="inline-block border border-black/15 font-bold px-8 py-3.5 rounded-full hover:border-ink transition-colors duration-300">
                Back to sign in
              </Link>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6 noise relative" data-testid="portal-auth">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-volt/15 blur-3xl rounded-full pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-white/50 hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Revora Reviews
        </Link>
        <div className="bg-white rounded-sm p-8">
          <p className="font-display text-2xl font-black text-ink mb-1">Business Portal</p>
          <p className="text-xs text-black/45 mb-7">Manage your Revora products, destinations and tap activity.</p>

          <div className="grid grid-cols-2 gap-1 bg-mist rounded-full p-1 mb-7" data-testid="portal-mode-toggle">
            {[["login", "Sign in"], ["signup", "Create account"]].map(([m, label]) => (
              <button
                key={m}
                data-testid={`portal-mode-${m}`}
                onClick={() => { setMode(m); setError(""); setNotice(""); }}
                className={`py-2 rounded-full text-sm font-bold transition-colors duration-200 ${mode === m ? "bg-ink text-white" : "text-black/50 hover:text-ink"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={submit}>
            <button
              type="button"
              data-testid="portal-google-button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/portal/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 border border-black/15 bg-white font-bold py-3.5 rounded-full hover:border-ink transition-colors duration-300 mb-5"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.6z"/><path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1l-3.9 3C3.1 21.3 7.2 24 12 24z"/><path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.3l3.9-3z"/><path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.2 0 3.1 2.7 1.3 6.7l3.9 3c.9-2.9 3.6-5 6.8-5z"/></svg>
              Continue with Google
            </button>
            <div className="flex items-center gap-3 mb-5">
              <span className="flex-1 h-px bg-black/10" />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-black/35">or with email</span>
              <span className="flex-1 h-px bg-black/10" />
            </div>
            {mode === "signup" && (
              <div className="mb-3">
                <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Business Name</label>
                <input data-testid="portal-business-name" className={INPUT} value={businessName} onChange={(e) => setBusinessName(e.target.value)} required minLength={2} />
              </div>
            )}
            <div className="mb-3">
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Email</label>
              <input data-testid="portal-email" type="email" className={INPUT} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Password {mode === "signup" && <span className="normal-case tracking-normal text-black/30">(8+ characters)</span>}</label>
              <input data-testid="portal-password" type="password" className={INPUT} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={mode === "signup" ? 8 : 1} />
            </div>
            {error && (
              <p data-testid="portal-error" className="text-xs text-red-500 mb-4">
                {error}{" "}
                {error.includes("verify") && (
                  <button type="button" onClick={resend} className="font-bold underline" data-testid="portal-resend-link">Resend link</button>
                )}
              </p>
            )}
            {notice && <p data-testid="portal-notice" className="text-xs text-green-600 mb-4">{notice}</p>}
            <button
              data-testid="portal-submit"
              disabled={busy}
              className="w-full bg-volt text-ink font-bold py-3.5 rounded-full hover:bg-voltdark hover:text-white disabled:opacity-40 transition-colors duration-300"
            >
              {busy ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
            </button>
          </form>
          <p className="text-[11px] text-black/40 mt-5 text-center">
            New accounts start on the free Starter plan. Verify your email, then claim orders placed with the same address.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
